import React, { useEffect, useState } from 'react';
import { Modal, Spin, Result } from 'antd';
import { Button, ButtonVariant } from '../../components/button';
import { ConfirmOutcome, OperationStatus } from '../../types';

interface ConfirmationModalProps {
    isVisible: boolean;
    title: string;
    description: React.ReactNode;
    confirmLabel?: string;
    confirmVariant?: ButtonVariant;
    /**
     * Returning nothing reports success. Return an outcome to report failure,
     * optionally with the server's own wording, which is shown in place of
     * `errorMessage`. The outcome exists because the store's network helper
     * resolves on a rejected request rather than throwing, so a caller has no
     * exception to let through.
     */
    onConfirm: () => Promise<void | ConfirmOutcome>;
    onClose: () => void;
    successMessage: string;
    errorMessage: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isVisible,
    title,
    description,
    confirmLabel = 'Confirm',
    confirmVariant = 'secondary-destructive',
    onConfirm,
    onClose,
    successMessage,
    errorMessage,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<{
        status: OperationStatus;
        title: string;
    } | null>(null);

    useEffect(() => {
        if (isVisible) {
            setIsLoading(false);
            setResult(null);
        }
    }, [isVisible]);

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            const outcome = await onConfirm();
            const failed = outcome ? !outcome.ok : false;
            setResult({
                status: failed
                    ? OperationStatus.ERROR
                    : OperationStatus.SUCCESS,
                title: failed
                    ? outcome?.message || errorMessage
                    : successMessage,
            });
        } catch (error) {
            setResult({
                status: OperationStatus.ERROR,
                title: errorMessage,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal title={title} open={isVisible} onCancel={onClose} footer={null}>
            {isLoading ? (
                <div style={{ textAlign: 'center', padding: '24px' }}>
                    <Spin size="large" />
                </div>
            ) : result ? (
                <Result
                    status={result.status}
                    title={result.title}
                    extra={[
                        <Button key="close" onClick={onClose}>
                            Close
                        </Button>,
                    ]}
                />
            ) : (
                <>
                    {description}
                    <div style={{ textAlign: 'right', marginTop: '24px' }}>
                        <Button
                            onClick={handleConfirm}
                            variant={confirmVariant}
                        >
                            {confirmLabel}
                        </Button>
                        <Button
                            onClick={onClose}
                            variant="secondary"
                            style={{ marginLeft: '10px' }}
                        >
                            Cancel
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};

export { ConfirmationModal };
